'use client'

import { cn } from '@brew/ui/cn'
import { type ComponentProps, type ReactNode, useId } from 'react'
import { Switch } from '@/components/ui/switch'

/**
 * Form building blocks for skinned dialogs and sheets: the form that spans
 * body + footer, one labelled field, section labels and toggle rows. Every
 * dialog field reads the same — label, control, then either the hint or
 * the error, both wired to the control.
 */

/** A form that holds a dialog's body and footer (submit lives in the footer). */
export function BrewDialogForm({
  className,
  ...props
}: ComponentProps<'form'>) {
  return (
    <form
      className={cn('flex min-h-0 flex-1 flex-col', className)}
      {...props}
    />
  )
}

const LABEL_CLASS = 'font-medium text-[var(--dlg-ink)] text-dialog-meta'

/** Props a field hands its control so the hint and error are announced. */
export type BrewFieldControlProps = {
  id: string
  'aria-describedby'?: string
  'aria-invalid'?: true
}

export function BrewField({
  label,
  htmlFor,
  hint,
  error,
  labelTrailing,
  srOnlyLabel = false,
  as = 'div',
  density = 'default',
  className,
  children,
}: {
  label: ReactNode
  /** The control's id when `children` is a node; a render prop gets one. */
  htmlFor?: string
  hint?: ReactNode
  /** Replaces the hint while set; the control is marked invalid. */
  error?: ReactNode
  /** Right of the label: a value readout, a count or a quiet action. */
  labelTrailing?: ReactNode
  srOnlyLabel?: boolean
  /** `fieldset` renders the label as a `legend` (radio groups, pickers). */
  as?: 'div' | 'fieldset'
  /** `compact` for sheets: smaller label, tighter stack. */
  density?: 'default' | 'compact'
  className?: string
  children: ReactNode | ((control: BrewFieldControlProps) => ReactNode)
}) {
  const generatedId = useId()
  const controlId = htmlFor ?? generatedId
  const messageId = `${controlId}-message`
  const message = error ?? hint
  const control: BrewFieldControlProps = {
    id: controlId,
    ...(message ? { 'aria-describedby': messageId } : {}),
    ...(error ? { 'aria-invalid': true as const } : {}),
  }
  const labelClass = cn(
    density === 'compact'
      ? 'font-medium text-[var(--dlg-body)] text-xs'
      : LABEL_CLASS,
    srOnlyLabel && 'sr-only'
  )
  const content = typeof children === 'function' ? children(control) : children
  const hasMessage = message != null && message !== false && message !== ''
  const hasTrailing = labelTrailing != null && labelTrailing !== false
  const labelNode =
    as === 'fieldset' ? (
      <legend className={labelClass}>{label}</legend>
    ) : (
      <label htmlFor={controlId} className={labelClass}>
        {label}
      </label>
    )
  const trailingNode = (
    <div className="flex shrink-0 items-center gap-2 text-[var(--dlg-muted)] text-xs">
      {labelTrailing}
    </div>
  )
  let labelRow: ReactNode = labelNode
  if (hasTrailing && as === 'fieldset') {
    // A legend names its fieldset only as a direct child: keep it there
    // (visually hidden) and show the label row beside the trailing slot.
    labelRow = (
      <>
        <legend className="sr-only">{label}</legend>
        <div className="flex items-center justify-between gap-3">
          <span aria-hidden="true" className={labelClass}>
            {label}
          </span>
          {trailingNode}
        </div>
      </>
    )
  } else if (hasTrailing) {
    labelRow = (
      <div className="flex items-center justify-between gap-3">
        {labelNode}
        {trailingNode}
      </div>
    )
  }
  const Root = as
  return (
    <Root
      className={cn(
        'flex min-w-0 flex-col',
        density === 'compact' ? 'gap-1.5' : 'gap-2',
        as === 'fieldset' && 'm-0 border-0 p-0',
        className
      )}
    >
      {labelRow}
      {content}
      {hasMessage ? (
        <p
          id={messageId}
          role={error ? 'alert' : undefined}
          className={cn(
            'text-xs leading-[17px]',
            error ? 'text-[var(--dlg-danger-ink)]' : 'text-[var(--dlg-muted)]'
          )}
        >
          {message}
        </p>
      ) : null}
    </Root>
  )
}

/** A heading inside a body or sheet section that labels no single control. */
export function BrewSectionLabel({
  children,
  description,
  trailing,
  as: Tag = 'div',
  className,
}: {
  children: ReactNode
  description?: ReactNode
  trailing?: ReactNode
  as?: 'div' | 'h3'
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <div className="flex items-center justify-between gap-3">
        <Tag className={LABEL_CLASS}>{children}</Tag>
        {trailing ? (
          <span className="shrink-0 text-[var(--dlg-muted)] text-xs">
            {trailing}
          </span>
        ) : null}
      </div>
      {description ? (
        <p className="text-[var(--dlg-body)] text-xs leading-[17px]">
          {description}
        </p>
      ) : null}
    </div>
  )
}

/** A tinted row: title, description and a switch. */
export function BrewToggleRow({
  title,
  description,
  checked,
  onCheckedChange,
  disabled,
  id,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  id?: string
  className?: string
}) {
  const generatedId = useId()
  const switchId = id ?? generatedId
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-[14px] bg-[var(--dlg-tile)] px-4 py-3',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <label
          htmlFor={switchId}
          className="font-medium text-[var(--dlg-ink)] text-sm leading-[18px]"
        >
          {title}
        </label>
        {description ? (
          <div className="text-[var(--dlg-body)] text-xs leading-[17px]">
            {description}
          </div>
        ) : null}
      </div>
      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  )
}
